import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Trophy, ChevronRight, Brain, Sparkles } from 'lucide-react';
import twygsLearnLogo from '../../../assets/clay_twygs_logo_no_bg.png';

interface TutorialCardProps {
  onStart: () => void;
}

const TutorialCard: React.FC<TutorialCardProps> = ({ onStart }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#FAF5F3] via-[#F5F6F4] to-[#FBF8F2] border border-[#E7D1C7] shadow-md hover:shadow-lg transition-shadow cursor-pointer"
      onClick={onStart}
    >
      {/* Decorative corner accent */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#B87968]/10 to-transparent rounded-bl-full" />
      <div className="absolute bottom-0 left-0 w-20 h-20 bg-gradient-to-tr from-[#798872]/10 to-transparent rounded-tr-full" />

      {/* Floating sparkle */}
      {/* <motion.div
        className="absolute top-4 right-4"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.6, 1, 0.6]
        }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Sparkles className="w-5 h-5 text-[#BF954F]" />
      </motion.div> */}

      <div className="relative p-4 sm:p-5">
        {/* Logo and content row */}
        <div className="flex items-center gap-4 flex-col md:flex-row">
          {/* Twygs Learn Logo */}
          <div className="flex-shrink-0">
            <img src={twygsLearnLogo} alt="Twygs Learn" className="h-10 sm:h-12 w-auto" />
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-10 bg-[#D9B5A5]/50" />

          {/* Text content */}
          <div className="flex-1 min-w-0">
            <p className="text-[#5F3930] text-sm sm:text-base font-medium leading-snug">
              Turn your scroll time into{' '}
              <span className="text-[#B87968] font-semibold">growth time</span>
            </p>
            <p className="text-[#7F4A3F]/70 text-xs mt-0.5">
              2-5 min bite-sized lessons • Earn seeds
            </p>
          </div>

          {/* CTA Button - Desktop */}
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onStart();
            }}
            className="hidden sm:flex flex-shrink-0 py-2.5 px-5 bg-gradient-to-r from-[#B87968] to-[#A05D4E] hover:from-[#A05D4E] hover:to-[#7F4A3F] text-white font-semibold rounded-xl items-center gap-1.5 shadow-md transition-all text-sm"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>See How</span>
            <ChevronRight className="w-4 h-4" />
          </motion.button>
        </div>

        {/* Benefits row */}
        <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#E7D1C7]/50">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/60 border border-[#D4D9CF] rounded-full text-[11px] font-medium text-[#4F594B]">
            <Zap className="w-3 h-3 text-[#BF954F]" />
            Quick lessons
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/60 border border-[#D4D9CF] rounded-full text-[11px] font-medium text-[#4F594B]">
            <Trophy className="w-3 h-3 text-[#BF954F]" />
            Earn seeds
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/60 border border-[#D4D9CF] rounded-full text-[11px] font-medium text-[#4F594B]">
            <Brain className="w-3 h-3 text-[#6B9AB3]" />
            Expand your mind
          </span>

          {/* Mobile CTA */}
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              onStart();
            }}
            className="sm:hidden ml-auto py-1.5 px-4 bg-gradient-to-r from-[#B87968] to-[#A05D4E] text-white font-semibold rounded-lg flex items-center gap-1 shadow-sm text-xs"
            whileTap={{ scale: 0.98 }}
          >
            <span>Start</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default TutorialCard;
